/**
 * 🚚 DELIVERY SECTION - Assistant Livraison + Offres Week-end
 * Section livraison avec bouton cyan clignotant + Offres conformes à la loi
 * 
 * ⚠️ CONFORMITÉ LOI ÉVIN / HAPPY HOURS:
 * - Pas de promotion de prix sur l'alcool
 * - Séparation claire des offres avec/sans alcool
 * - Mention légale obligatoire
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Truck, ArrowRight, Wine, Coffee, AlertTriangle, X, PartyPopper, Sparkles } from 'lucide-react';

// Formules Week-end conformes à la loi
const FORMULES_WEEKEND = {
  sansAlcool: [
    { 
      name: "Pack Soirée Soft", 
      description: "Chips, bonbons, sodas & limonades",
      emoji: "🥤",
      color: "from-green-500/20 to-emerald-500/20",
      border: "border-green-500/40"
    },
    { 
      name: "Pack Cinéma Maison", 
      description: "Pop-corn, glaces, boissons fraîches",
      emoji: "🍿",
      color: "from-yellow-500/20 to-orange-500/20",
      border: "border-yellow-500/40"
    },
    { 
      name: "Pack Goûter Gourmand", 
      description: "Gâteaux, biscuits, jus de fruits",
      emoji: "🧁",
      color: "from-pink-500/20 to-rose-500/20",
      border: "border-pink-500/40"
    }
  ],
  avecAlcool: [
    { 
      name: "Pack Apéro", 
      description: "Bières artisanales, chips, cacahuètes",
      emoji: "🍺",
      color: "from-amber-500/20 to-yellow-500/20",
      border: "border-amber-500/40"
    },
    { 
      name: "Pack Vin & Fromage", 
      description: "Bouteille de vin, assortiment fromages",
      emoji: "🍷",
      color: "from-red-500/20 to-rose-500/20",
      border: "border-red-500/40"
    },
    { 
      name: "Pack Soirée Premium", 
      description: "Champagne, verrines, petits fours",
      emoji: "🍾",
      color: "from-purple-500/20 to-pink-500/20",
      border: "border-purple-500/40"
    }
  ]
};

const DeliverySection = () => {
  const navigate = useNavigate();
  const [showFormules, setShowFormules] = useState(false);
  const [activeTab, setActiveTab] = useState('sansAlcool');

  return (
    <section className="relative py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Bouton Livraison CYAN CLIGNOTANT */}
          <motion.button
            onClick={() => navigate('/livraison')}
            className="relative p-6 rounded-3xl overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(6, 182, 212, 0.05) 100%)',
              border: '2px solid rgba(6, 182, 212, 0.4)'
            }}
            animate={{
              boxShadow: [
                '0 0 20px rgba(6, 182, 212, 0.3)',
                '0 0 40px rgba(6, 182, 212, 0.6)',
                '0 0 20px rgba(6, 182, 212, 0.3)'
              ],
              borderColor: [
                'rgba(6, 182, 212, 0.4)',
                'rgba(6, 182, 212, 0.8)',
                'rgba(6, 182, 212, 0.4)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="delivery-button"
          >
            {/* Shine effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />

            <div className="relative flex items-center gap-4">
              <motion.div
                className="w-16 h-16 rounded-2xl bg-cyan-500/20 flex items-center justify-center"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <Truck className="w-8 h-8 text-cyan-400" />
              </motion.div>
              
              <div className="text-left flex-1">
                <h3 className="text-xl font-black text-white mb-1">🚚 Je me fais livrer !</h3>
                <p className="text-cyan-400 font-semibold">GRATUIT • Vendredi & Samedi</p>
                <p className="text-cyan-300/70 text-sm">🌙 Ramadan: 21h-4h du matin</p>
              </div>

              <ArrowRight className="w-6 h-6 text-cyan-400 group-hover:translate-x-2 transition-transform" />
            </div>
          </motion.button>

          {/* Offres Week-end - Cliquable pour voir les formules */}
          <motion.button
            onClick={() => setShowFormules(true)}
            className="p-6 rounded-3xl text-left group"
            style={{
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
              border: '2px solid rgba(236, 72, 153, 0.3)'
            }}
            whileHover={{ 
              scale: 1.02,
              boxShadow: '0 0 30px rgba(236, 72, 153, 0.4)'
            }}
            whileTap={{ scale: 0.98 }}
            data-testid="weekend-offers-button"
          >
            <div className="flex items-center gap-4">
              <motion.div
                className="w-16 h-16 rounded-2xl bg-pink-500/20 flex items-center justify-center text-3xl"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                🎉
              </motion.div>
              
              <div className="flex-1">
                <h3 className="text-xl font-black text-white mb-1">Offres Week-end</h3>
                <p className="text-pink-400 font-medium">Formules avec & sans alcool</p>
                <p className="text-pink-300/60 text-xs mt-1">Cliquez pour découvrir</p>
              </div>
              
              <Sparkles className="w-6 h-6 text-pink-400 group-hover:rotate-12 transition-transform" />
            </div>
          </motion.button>
        </div>
      </div>

      {/* MODAL - Formules Week-end */}
      <AnimatePresence>
        {showFormules && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowFormules(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl"
              style={{
                background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 100%)',
                border: '2px solid rgba(236, 72, 153, 0.3)'
              }}
            >
              {/* Header */}
              <div className="sticky top-0 z-10 p-6 border-b border-pink-500/20 bg-gradient-to-b from-[#0a0a1a] to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <PartyPopper className="w-8 h-8 text-pink-400" />
                    <div>
                      <h2 className="text-2xl font-black text-white">Formules Week-end</h2>
                      <p className="text-pink-400 text-sm">Vendredi & Samedi uniquement</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowFormules(false)}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setActiveTab('sansAlcool')}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                      activeTab === 'sansAlcool'
                        ? 'bg-green-500/30 text-green-400 border-2 border-green-500/50'
                        : 'bg-white/5 text-gray-400 border-2 border-transparent hover:bg-white/10'
                    }`}
                  >
                    <Coffee className="w-5 h-5" />
                    Sans alcool
                  </button>
                  <button
                    onClick={() => setActiveTab('avecAlcool')}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                      activeTab === 'avecAlcool'
                        ? 'bg-red-500/30 text-red-400 border-2 border-red-500/50'
                        : 'bg-white/5 text-gray-400 border-2 border-transparent hover:bg-white/10'
                    }`}
                  >
                    <Wine className="w-5 h-5" />
                    Avec alcool (18+)
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="p-6 space-y-4">
                {/* Formules */}
                {FORMULES_WEEKEND[activeTab].map((formule, index) => (
                  <motion.div
                    key={formule.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-5 rounded-2xl bg-gradient-to-r ${formule.color} border-2 ${formule.border}`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-4xl">{formule.emoji}</span>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white">{formule.name}</h3>
                        <p className="text-gray-300 text-sm">{formule.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}

                {/* Mention légale pour l'alcool */}
                {activeTab === 'avecAlcool' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-red-300/80 space-y-1">
                        <p className="font-bold text-red-400">⚠️ MENTIONS LÉGALES</p>
                        <p>• L'abus d'alcool est dangereux pour la santé, à consommer avec modération.</p>
                        <p>• La vente d'alcool est interdite aux mineurs de moins de 18 ans.</p>
                        <p>• Une pièce d'identité sera demandée à la livraison.</p>
                        <p className="text-red-400/60 italic mt-2">Conformément à la loi Évin et aux réglementations sur la vente d'alcool.</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Info livraison */}
                <div className="mt-4 p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
                  <p className="text-cyan-400 text-sm text-center">
                    🚚 Livraison GRATUITE 🌙 Ramadan: Ven-Sam 21h-4h !
                  </p>
                </div>

                {/* Bouton Commander */}
                <motion.button
                  onClick={() => {
                    setShowFormules(false);
                    navigate('/livraison');
                  }}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 
                             text-white font-bold text-lg hover:from-pink-600 hover:to-purple-600 
                             transition-all shadow-lg shadow-pink-500/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  🛵 Commander ma formule
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default DeliverySection;
